import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, Transition as _Transition, withCtx as _withCtx, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "app-layout" }
const _hoisted_2 = { class: "navbar" }
const _hoisted_3 = { class: "navbar-inner" }
const _hoisted_4 = { class: "navbar-left" }
const _hoisted_5 = { class: "navbar-title" }
const _hoisted_6 = { class: "navbar-title-text" }
const _hoisted_7 = { class: "navbar-actions" }
const _hoisted_8 = ["aria-label"]
const _hoisted_9 = { class: "navbar-user-avatar" }
const _hoisted_10 = { class: "navbar-user-meta" }
const _hoisted_11 = { class: "navbar-user-name" }
const _hoisted_12 = { class: "navbar-user-role" }
const _hoisted_13 = {
  key: 0,
  class: "navbar-dropdown"
}
const _hoisted_14 = { class: "navbar-dropdown-header" }
const _hoisted_15 = { class: "navbar-dropdown-avatar" }
const _hoisted_16 = { class: "navbar-dropdown-name" }
const _hoisted_17 = { class: "navbar-dropdown-email" }
const _hoisted_18 = { class: "navbar-dropdown-items" }
const _hoisted_19 = { class: "navbar-dropdown-items" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = { class: "app-content" }
const _hoisted_22 = { class: "app-content-inner" }

import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useClickOutside } from '@/composables/ui/useClickOutside'
import { useUser } from '@/composables/auth/useUser'
import { useLogin } from '@/composables/auth/useLogin'
import { useRouter } from 'vue-router'
import Sidebar from './Sidebar.vue'
import { BaseToast, BasePageLoader } from '@/components/ui'
import {
  Menu, Sun, Moon, ChevronDown,
  User, LogOut
} from 'lucide-vue-next'

// Stores & Composables

export default /*@__PURE__*/_defineComponent({
  __name: 'AppLayout',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const themeStore = useThemeStore()
const { userInitials, currentUser } = useUser()
const { handleLogout } = useLogin()

// State
const isProfileOpen = ref(false)
const isSidebarOpen = ref(false)
const sidebarCollapsed = ref(false)
const userMenuRef = ref<HTMLElement | null>(null)

// Computed
const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Diasporiques ABA Solutions LLC')
const isLoading = computed(() => authStore.isLoading)
const isDarkMode = computed(() => themeStore.isDarkMode)
const pageTitle = computed(() => (route.meta?.title as string) || 'Dashboard')

// Methods
function toggleProfile() {
  isProfileOpen.value = !isProfileOpen.value
}

function handleProfileAction(action: string) {
  isProfileOpen.value = false
  if (action === 'profile') {
    router.push('/profile')
  }
}

function toggleSidebar() {
  isSidebarOpen.value = !isSidebarOpen.value
}

function toggleTheme() {
  themeStore.toggleTheme()
}

// Click outside to close dropdown
const { handleClickOutside } = useClickOutside(isProfileOpen, userMenuRef)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Sidebar, {
      isOpen: isSidebarOpen.value,
      "onUpdate:isOpen": _cache[0] || (_cache[0] = ($event: any) => ((isSidebarOpen).value = $event)),
      collapsed: sidebarCollapsed.value,
      "onUpdate:collapsed": _cache[1] || (_cache[1] = ($event: any) => ((sidebarCollapsed).value = $event))
    }, null, 8, ["isOpen", "collapsed"]),
    _createElementVNode("div", {
      class: _normalizeClass(["app-main", { 'app-main--sidebar-collapsed': sidebarCollapsed.value }])
    }, [
      _createElementVNode("header", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("button", {
              type: "button",
              class: "navbar-menu-btn",
              onClick: toggleSidebar,
              "aria-label": "Toggle sidebar"
            }, [
              _createVNode(_unref(Menu), { class: "h-5 w-5" })
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("h1", _hoisted_6, _toDisplayString(pageTitle.value), 1)
            ])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("button", {
              type: "button",
              class: "navbar-icon-btn",
              onClick: toggleTheme,
              "aria-label": isDarkMode.value ? 'Switch to light mode' : 'Switch to dark mode'
            }, [
              (isDarkMode.value)
                ? (_openBlock(), _createBlock(_unref(Sun), {
                    key: 0,
                    class: "h-5 w-5"
                  }))
                : (_openBlock(), _createBlock(_unref(Moon), {
                    key: 1,
                    class: "h-5 w-5"
                  }))
            ], 8, _hoisted_8),
            _cache[9] || (_cache[9] = _createElementVNode("div", { class: "navbar-divider" }, null, -1)),
            _createElementVNode("div", {
              class: "navbar-user",
              ref_key: "userMenuRef",
              ref: userMenuRef
            }, [
              _createElementVNode("button", {
                type: "button",
                class: "navbar-user-btn",
                onClick: toggleProfile,
                "aria-label": "User menu"
              }, [
                _createElementVNode("div", _hoisted_9, _toDisplayString(_unref(userInitials)), 1),
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(currentUser)?.name || 'User'), 1),
                  _createElementVNode("span", _hoisted_12, _toDisplayString(_unref(currentUser)?.role || 'User'), 1)
                ]),
                _createVNode(_unref(ChevronDown), {
                  class: _normalizeClass(["h-4 w-4 navbar-user-chevron", { 'rotate-180': isProfileOpen.value }])
                }, null, 8, ["class"])
              ]),
              _createVNode(_Transition, {
                "enter-active-class": "transition-all duration-200 ease-out",
                "enter-from-class": "opacity-0 translate-y-1 scale-95",
                "enter-to-class": "opacity-100 translate-y-0 scale-100",
                "leave-active-class": "transition-all duration-150 ease-in",
                "leave-from-class": "opacity-100 translate-y-0 scale-100",
                "leave-to-class": "opacity-0 translate-y-1 scale-95"
              }, {
                default: _withCtx(() => [
                  (isProfileOpen.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                        _createElementVNode("div", _hoisted_14, [
                          _createElementVNode("div", _hoisted_15, _toDisplayString(_unref(userInitials)), 1),
                          _createElementVNode("div", null, [
                            _createElementVNode("p", _hoisted_16, _toDisplayString(_unref(currentUser)?.name || 'User'), 1),
                            _createElementVNode("p", _hoisted_17, _toDisplayString(_unref(currentUser)?.email || 'user@example.com'), 1)
                          ])
                        ]),
                        _cache[5] || (_cache[5] = _createElementVNode("div", { class: "navbar-dropdown-divider" }, null, -1)),
                        _createElementVNode("div", _hoisted_18, [
                          (_unref(currentUser)?.role !== 'admin')
                            ? (_openBlock(), _createElementBlock("button", {
                                key: 0,
                                class: "navbar-dropdown-item",
                                onClick: _cache[2] || (_cache[2] = ($event: any) => (handleProfileAction('profile')))
                              }, [
                                _createVNode(_unref(User), { class: "h-4 w-4" }),
                                _cache[4] || (_cache[4] = _createElementVNode("span", null, "My Profile", -1))
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("button", {
                            class: "navbar-dropdown-item",
                            onClick: toggleTheme
                          }, [
                            (isDarkMode.value)
                              ? (_openBlock(), _createBlock(_unref(Sun), {
                                  key: 0,
                                  class: "h-4 w-4"
                                }))
                              : (_openBlock(), _createBlock(_unref(Moon), {
                                  key: 1,
                                  class: "h-4 w-4"
                                })),
                            _createElementVNode("span", null, _toDisplayString(isDarkMode.value ? 'Light Mode' : 'Dark Mode'), 1)
                          ])
                        ]),
                        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "navbar-dropdown-divider" }, null, -1)),
                        _createElementVNode("div", _hoisted_19, [
                          _createElementVNode("button", {
                            class: "navbar-dropdown-item navbar-dropdown-item--danger",
                            onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(handleLogout) && _unref(handleLogout)(...args))),
                            disabled: isLoading.value
                          }, [
                            _createVNode(_unref(LogOut), { class: "h-4 w-4" }),
                            _createElementVNode("span", null, _toDisplayString(isLoading.value ? 'Signing out...' : 'Sign out'), 1)
                          ], 8, _hoisted_20)
                        ]),
                        _cache[7] || (_cache[7] = _createElementVNode("div", { class: "navbar-dropdown-divider" }, null, -1)),
                        _cache[8] || (_cache[8] = _createElementVNode("div", { class: "navbar-dropdown-credit" }, " Developed by Proweaver Inc. ", -1))
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ], 512)
          ])
        ])
      ]),
      _createElementVNode("main", _hoisted_21, [
        _createElementVNode("div", _hoisted_22, [
          _createVNode(_component_router_view)
        ])
      ])
    ], 2),
    _createVNode(_unref(BaseToast)),
    _createVNode(_unref(BasePageLoader))
  ]))
}
}

})